"use client"

import { useEffect, useRef, useState } from "react"
import { motion, useScroll, useTransform } from "framer-motion"

interface GoldenThreadProps {
  className?: string
}

/**
 * Golden Thread System
 *
 * A unifying visual element that connects sections of the page,
 * creating visual continuity and guiding the user's journey.
 *
 * The thread originates from the hero scroll indicator and flows
 * through each section, adapting its form:
 * - Hero → Vertical descent
 * - Gap → Horizontal bridges connecting concepts
 * - Approach → Journey pathway
 * - Work → Connecting case studies
 */
export function GoldenThread({ className = "" }: GoldenThreadProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [pathLength, setPathLength] = useState(0)

  const { scrollYProgress } = useScroll()

  // Animate path drawing based on scroll
  const strokeDashoffset = useTransform(scrollYProgress, [0, 1], [1000, 0])

  const opacity = useTransform(scrollYProgress, [0, 0.05, 0.95, 1], [0, 0.6, 0.6, 0])

  return (
    <motion.div
      ref={containerRef}
      className={`fixed left-1/2 top-0 -translate-x-1/2 w-1 h-screen pointer-events-none z-[5] ${className}`}
      style={{ opacity }}
    >
      {/* Main vertical thread */}
      <motion.div
        className="absolute left-1/2 -translate-x-1/2 w-px h-full"
        style={{
          background: `linear-gradient(
            to bottom,
            transparent 0%,
            var(--color-accent-gold) 10%,
            var(--color-accent-gold) 90%,
            transparent 100%
          )`,
          opacity: 0.3,
        }}
      />

      {/* Animated glow that follows scroll */}
      <motion.div
        className="absolute left-1/2 -translate-x-1/2 w-2 h-32 rounded-full"
        style={{
          background: `radial-gradient(
            ellipse at center,
            var(--color-accent-gold) 0%,
            transparent 70%
          )`,
          filter: "blur(4px)",
          top: useTransform(scrollYProgress, [0, 1], ["0%", "70%"]),
        }}
      />
    </motion.div>
  )
}

/**
 * Section Connector
 *
 * A decorative element placed between sections to create
 * visual continuity. Shows a flowing golden line with nodes.
 */
interface SectionConnectorProps {
  variant?: "simple" | "branching" | "converging"
  className?: string
}

export function SectionConnector({ variant = "simple", className = "" }: SectionConnectorProps) {
  const connectorRef = useRef<HTMLDivElement>(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { threshold: 0.3 }
    )

    if (connectorRef.current) {
      observer.observe(connectorRef.current)
    }

    return () => observer.disconnect()
  }, [])

  if (variant === "simple") {
    return (
      <div
        ref={connectorRef}
        className={`relative h-24 flex items-center justify-center ${className}`}
      >
        {/* Vertical line */}
        <motion.div
          className="absolute w-px h-full"
          style={{
            background: `linear-gradient(
              to bottom,
              transparent,
              var(--color-accent-gold),
              transparent
            )`,
          }}
          initial={{ scaleY: 0 }}
          animate={{ scaleY: isVisible ? 1 : 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        />

        {/* Center node */}
        <motion.div
          className="relative w-3 h-3 rounded-full border border-[var(--color-accent-gold)]"
          style={{
            backgroundColor: "var(--color-bg-primary)",
            boxShadow: "0 0 10px rgba(201, 162, 39, 0.3)",
          }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: isVisible ? 1 : 0, opacity: isVisible ? 1 : 0 }}
          transition={{ duration: 0.5, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    )
  }

  if (variant === "branching") {
    return (
      <div
        ref={connectorRef}
        className={`relative h-32 flex items-center justify-center ${className}`}
      >
        <svg
          className="w-full max-w-md h-full"
          viewBox="0 0 400 100"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Main stem */}
          <motion.line
            x1="200"
            y1="0"
            x2="200"
            y2="50"
            stroke="var(--color-accent-gold)"
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: isVisible ? 1 : 0 }}
            transition={{ duration: 0.5 }}
          />

          {/* Left branch */}
          <motion.path
            d="M 200 50 Q 150 50 100 80"
            fill="none"
            stroke="var(--color-accent-gold)"
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: isVisible ? 1 : 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          />

          {/* Right branch */}
          <motion.path
            d="M 200 50 Q 250 50 300 80"
            fill="none"
            stroke="var(--color-accent-gold)"
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: isVisible ? 1 : 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          />

          {/* Center node */}
          <motion.circle
            cx="200"
            cy="50"
            r="4"
            fill="var(--color-bg-primary)"
            stroke="var(--color-accent-gold)"
            strokeWidth="1"
            initial={{ scale: 0 }}
            animate={{ scale: isVisible ? 1 : 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          />

          {/* End nodes */}
          <motion.circle
            cx="100"
            cy="80"
            r="3"
            fill="var(--color-accent-gold)"
            initial={{ scale: 0 }}
            animate={{ scale: isVisible ? 1 : 0 }}
            transition={{ duration: 0.4, delay: 0.6 }}
          />
          <motion.circle
            cx="300"
            cy="80"
            r="3"
            fill="var(--color-accent-gold)"
            initial={{ scale: 0 }}
            animate={{ scale: isVisible ? 1 : 0 }}
            transition={{ duration: 0.4, delay: 0.6 }}
          />
        </svg>
      </div>
    )
  }

  if (variant === "converging") {
    return (
      <div
        ref={connectorRef}
        className={`relative h-32 flex items-center justify-center ${className}`}
      >
        <svg
          className="w-full max-w-md h-full"
          viewBox="0 0 400 100"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Left converging line */}
          <motion.path
            d="M 100 20 Q 150 50 200 50"
            fill="none"
            stroke="var(--color-accent-gold)"
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: isVisible ? 1 : 0 }}
            transition={{ duration: 0.6 }}
          />

          {/* Right converging line */}
          <motion.path
            d="M 300 20 Q 250 50 200 50"
            fill="none"
            stroke="var(--color-accent-gold)"
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: isVisible ? 1 : 0 }}
            transition={{ duration: 0.6 }}
          />

          {/* Main stem down */}
          <motion.line
            x1="200"
            y1="50"
            x2="200"
            y2="100"
            stroke="var(--color-accent-gold)"
            strokeWidth="1"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: isVisible ? 1 : 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          />

          {/* Convergence node with glow */}
          <motion.circle
            cx="200"
            cy="50"
            r="5"
            fill="var(--color-bg-primary)"
            stroke="var(--color-accent-gold)"
            strokeWidth="1.5"
            style={{
              filter: "drop-shadow(0 0 6px rgba(201, 162, 39, 0.5))",
            }}
            initial={{ scale: 0 }}
            animate={{ scale: isVisible ? 1 : 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          />

          {/* Start nodes */}
          <motion.circle
            cx="100"
            cy="20"
            r="2"
            fill="var(--color-accent-gold)"
            opacity="0.6"
            initial={{ scale: 0 }}
            animate={{ scale: isVisible ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          />
          <motion.circle
            cx="300"
            cy="20"
            r="2"
            fill="var(--color-accent-gold)"
            opacity="0.6"
            initial={{ scale: 0 }}
            animate={{ scale: isVisible ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          />
        </svg>
      </div>
    )
  }

  return null
}

/**
 * Thread Particle
 *
 * Small glowing particles that travel along the golden thread,
 * creating a sense of energy and flow.
 */
export function ThreadParticle({ delay = 0 }: { delay?: number }) {
  return (
    <motion.div
      className="absolute left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
      style={{
        backgroundColor: "var(--color-accent-gold)",
        boxShadow: "0 0 8px var(--color-accent-gold), 0 0 16px var(--color-accent-gold)",
      }}
      initial={{ top: "0%", opacity: 0 }}
      animate={{
        top: ["0%", "100%"],
        opacity: [0, 1, 1, 0],
      }}
      transition={{
        duration: 4,
        delay,
        repeat: Infinity,
        ease: "linear",
      }}
    />
  )
}
